import {
  CELL_H,
  CELL_W,
  COL_W,
  EXPANDED_SLOT_COLS,
  GRID_GAP_PX,
  GRID_PAD_X,
  GRID_PAD_Y,
  ROW_H,
} from './hexTheme'

export type HexLayoutItem = {
  id: string
  x: number
  y: number
  width: number
  height: number
  slots: number
  col: number
  row: number
}

export type HoneycombLayout = {
  items: HexLayoutItem[]
  contentWidth: number
  contentHeight: number
  cols: number
}

/**
 * Staggered pointy-top honeycomb packing (same model as the old canvas grid).
 * Expanded items occupy EXPANDED_SLOT_COLS consecutive column slots and push
 * following items to the next free slot / row.
 */
export function computeHoneycombLayout(
  ids: string[],
  expandedId: string | null,
  containerWidth = 0,
): HoneycombLayout {
  const count = ids.length

  // Pack as many columns as the available width allows so the grid fills the
  // space horizontally. We reserve the odd-row half-column stagger + a full
  // hex + trailing gap so the widest (odd) row never overflows -> no
  // horizontal scrolling. Falls back to a square-ish grid before measurement.
  let cols: number
  if (containerWidth > 0) {
    const usable = containerWidth - CELL_W - COL_W / 2 - GRID_GAP_PX - GRID_PAD_X * 2
    cols = Math.max(1, Math.floor(usable / COL_W) + 1)
  } else {
    cols = Math.max(1, Math.ceil(Math.sqrt(count * 1.25)))
  }

  let col = 0
  let row = 0
  let maxRight = 0
  let maxBottom = 0

  const items: HexLayoutItem[] = []

  for (const id of ids) {
    const slots = id === expandedId ? EXPANDED_SLOT_COLS : 1

    // Wrap to next row if this item won't fit on the current row.
    if (col + slots > cols) {
      col = 0
      row += 1
    }

    const odd = row % 2 === 1
    // Brick/honeycomb stagger: odd rows shift by half a column step.
    const offsetX = odd ? COL_W / 2 : 0
    const x = col * COL_W + offsetX + GRID_PAD_X
    const y = row * ROW_H + GRID_PAD_Y
    const width = slots * COL_W - GRID_GAP_PX
    const height = CELL_H

    items.push({ id, x, y, width, height, slots, col, row })

    maxRight = Math.max(maxRight, x + width)
    maxBottom = Math.max(maxBottom, y + height)

    col += slots
    if (col >= cols) {
      col = 0
      row += 1
    }
  }

  // Odd-row stagger can extend a half-column past the even-row edge.
  const staggerPad = COL_W / 2
  return {
    items,
    contentWidth: Math.max(maxRight + GRID_PAD_X + GRID_GAP_PX + staggerPad, CELL_W + GRID_GAP_PX + GRID_PAD_X * 2),
    contentHeight: Math.max(maxBottom + GRID_PAD_Y + GRID_GAP_PX, CELL_H + GRID_PAD_Y * 2 + GRID_GAP_PX),
    cols,
  }
}
